/**
 * @module HubTable/tokens
 *
 * Layout constants, density presets, z-index stacking, and typography tokens
 * shared across {@link HubTable} and its cell components.
 */

import type { HubTableRowHeight } from './types';

export const HUB_TABLE_CHECKBOX_WIDTH = 36;

/** Shared layout for sticky checkbox column — centers control in fixed-width column */
export const HUB_TABLE_CHECKBOX_CELL_CLASS =
  'overflow-hidden border-b border-slate-200 py-0 align-middle';

export const HUB_TABLE_CHECKBOX_INNER_CLASS =
  'flex h-full w-full items-center justify-center';

export const HUB_TABLE_CHECKBOX_SIZE_CLASS = 'h-4 w-4';

/**
 * Fixed row/header heights (px) per density tier.
 *
 * Pick tier by table role, not page:
 * - `comfortable` — hub list pages with actions/pills (~7 columns)
 * - `compact` — data grids with many columns and 50+ rows
 * - `tall` — multi-line / avatar-heavy rows
 */
export const HUB_TABLE_ROW_HEIGHT: Record<HubTableRowHeight, number> = {
  compact: 36,
  comfortable: 44,
  relaxed: 56,
  tall: 76,
};

/** Semantic aliases — use instead of raw string literals at call sites. */
export const HUB_TABLE_DENSITY = {
  /** /audiences — fewer columns, inline actions */
  list: 'comfortable',
  /** /audiences/:id contacts, signals, prospects — many columns, high row count */
  dataGrid: 'compact',
  /** /campaigns — name + compact progress subline */
  campaign: 'relaxed',
  /** Avatar + secondary line cells */
  multiLine: 'tall',
} as const satisfies Record<string, HubTableRowHeight>;

export function hubTableHeaderHeight(rowHeight: HubTableRowHeight): number {
  if (rowHeight === 'compact') return 36;
  if (rowHeight === 'comfortable' || rowHeight === 'relaxed') return 40;
  return 44;
}

export function hubTableCellPadding(rowHeight: HubTableRowHeight): string {
  return rowHeight === 'compact' ? 'px-3' : 'px-4';
}

export const HUB_TABLE_STICKY_NAME_LEFT = HUB_TABLE_CHECKBOX_WIDTH;

/** Stacking order: body sticky columns < header row < header name < header corner (checkbox). */
export const HUB_TABLE_Z = {
  stickyBody: 3,
  stickyHeader: 10,
  stickyHeaderName: 15,
  stickyHeaderCorner: 20,
} as const;

export const HUB_TABLE_SELECTED_BG = '#f0f7ff';

/**
 * Page chrome aligned with {@link HUB_TABLE_TYPO} — same horizontal inset as table
 * cells (`px-4`), smaller vertical rhythm than legacy `px-responsive` headers.
 *
 * Use on all ProtectedLayout hub pages (`PageHeader` default, `ToolbarLayout variant="hub"`).
 */
export const HUB_PAGE_CHROME = {
  /** Matches {@link hubTableCellPadding} comfortable tier — keeps chrome flush with table */
  paddingX: 'px-4',
  /**
   * Vertical padding + min-height so the header band balances the compact floating
   * notifications + avatar cluster in ProtectedLayout.
   */
  headerPaddingY: 'py-3.5',
  /** Exact desktop header height shared by custom hub toolbars. */
  headerHeight: 'h-[68px]',
  headerMinHeight: 'min-h-[68px]',
  /**
   * Keeps title/description from running under the absolute-positioned nav on md+.
   * `--top-nav-inset` is measured and published by ProtectedLayout so page actions
   * preserve a consistent gutter beside the floating cluster. Falls back to the old
   * 20rem via the `:root` default in globals.css if the measurement hasn't landed.
   */
  headerReserveRight: 'md:pr-[var(--top-nav-inset)]',
  toolbarPaddingY: 'py-2',
  title: 'text-[17px] font-semibold leading-6 text-slate-900',
  description: 'text-[13px] leading-snug text-slate-500',
  iconBox: 'h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm',
  icon: 'h-[18px] w-[18px] text-white',
  /** Toolbar controls — h-8 + text-xs to match table body scale on md+ */
  controlHeight: 'h-8',
  toolbarButton:
    'h-8 px-3 text-xs border-slate-200 bg-white shadow-sm hover:border-slate-300 shrink-0',
  /**
   * Buttons on a sub-nav row that also carries a tab strip (Campaigns > Campaigns/Templates):
   * h-9 to match the tab strip's own height, so one row reads as one row. `toolbarButton` is
   * the h-8 variant for a toolbar that is a band of its own underneath the tabs.
   */
  rowButton:
    'h-9 px-3 text-[13px] border-slate-200 bg-white shadow-sm hover:border-slate-300 shrink-0',
} as const;

/**
 * Visual hierarchy for hub list pages (/audiences, /campaigns, …)
 *
 * Page chrome uses {@link HUB_PAGE_CHROME} (17px title, 13px meta, ~68px header band).
 * Table: primary column > body > meta headers/footer > never below 12px in cells.
 */
export const HUB_TABLE_TYPO = {
  /** Column headers — secondary, above body */
  header: 'text-xs font-semibold uppercase tracking-wide text-slate-500',
  /** Default cell data */
  body: 'text-[13px] leading-snug text-slate-700',
  /** Primary identifier column (name) */
  bodyPrimary: 'text-[14px] font-medium leading-snug text-slate-900',
  /** Dates, creator, secondary facts */
  bodyMuted: 'text-[13px] leading-snug text-slate-600',
  /** Footer range info, pagination helper */
  meta: 'text-[13px] text-slate-500',
  /** FloatingSelectionBar cross-page prompt link */
  bulkLabel: 'text-[13px] font-semibold text-blue-600',
  /** Inline action controls (Add Campaign, Trigger, …) */
  action: 'text-xs font-semibold',
  /** Action that displays entity name (campaign pill) */
  actionContent: 'text-[13px] font-medium',
} as const;

/** @deprecated use HUB_TABLE_TYPO.body */
export const HUB_TABLE_BODY_TEXT_CLASS = HUB_TABLE_TYPO.body;

/** @deprecated use HUB_TABLE_TYPO.header */
export const HUB_TABLE_HEADER_TEXT_CLASS = HUB_TABLE_TYPO.header;

/**
 * Sidebar nav typography — aligned with {@link HUB_PAGE_CHROME} and {@link HUB_TABLE_TYPO}.
 * Uses Inter (via `font-inter`) and slate palette like page headers and table content.
 */
export const SIDEBAR_NAV_TYPO = {
  sectionHeader: HUB_TABLE_TYPO.header,
  item: 'font-inter text-sm font-medium leading-snug text-slate-700',
  itemActive: 'font-inter text-sm font-semibold leading-snug text-slate-900',
  itemMuted: 'font-inter text-sm font-medium leading-snug text-slate-500',
  workspaceName: 'font-inter text-sm font-medium leading-snug text-slate-900',
  utility: 'font-inter text-sm font-medium text-slate-600',
} as const;
